import { useState, useEffect, useContext } from 'react';
import { fetchRegions, fetchCommunes } from '../services/location_services';
import AuthContext from 'store/context/PublicContext';
import { useLocation } from 'react-router-dom';

export const useLocations = (country: string, selectedCommune: string | null) => {
  const [regions, setRegions] = useState<string[]>([]);
  const [communes, setCommunes] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [isLoadingCommunes, setIsLoadingCommunes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [communeCoordinates, setCommuneCoordinates] = useState<{ 
    center: { latitude: number; longitude: number };
    area?: any;
    extras?: any;
  } | null>(null);
  const [communeGeoError, setCommuneGeoErrorState] = useState<string | null>(null);
  const [errorCommune, setErrorCommune] = useState<string | null>(null);

  // Derived state: only return error if it belongs to the currently selected commune
  const effectiveCommuneGeoError = selectedCommune === errorCommune ? communeGeoError : null;

  const { user } = useContext(AuthContext);
  const token = user?.token;

  const { pathname } = useLocation();

  useEffect(() => {
    const loadRegions = async () => {
      // Only fetch if token is available AND we are on the specific map route
      if (!token || !pathname.includes('/coverages/map')) return;

      try {
        setIsLoadingRegions(true);
        setError(null);
        const data = await fetchRegions(country, token);
        setRegions(data);
      } catch (err) {
        setError('Error al cargar las regiones');
        setRegions([]);
      } finally {
        setIsLoadingRegions(false);
      }
    };

    if (country) {
      loadRegions();
    }
  }, [country, token, pathname]);

  useEffect(() => {
    const loadCommunes = async () => {
      // Only fetch if token is available AND we are on the specific map route
      if (!selectedRegion || !token || !pathname.includes('/coverages/map')) {
        setCommunes([]);
        return;
      }

      try {
        setIsLoadingCommunes(true);
        setError(null);
        const data = await fetchCommunes(country, selectedRegion, token);
        setCommunes(data);
      } catch (err) {
        setError('Error al cargar las comunas');
        setCommunes([]);
      } finally {
        setIsLoadingCommunes(false);
      }
    };

    if (country && selectedRegion) {
      loadCommunes();
    }
  }, [country, selectedRegion, token, pathname]); // Added selectedRegion to dependencies

  useEffect(() => {
    const loadCommuneDetails = async () => {
      // Reset error and coordinates when selection changes
      setCommuneGeoErrorState(null);
      setErrorCommune(null);

      if (!selectedCommune || !token) {
        setCommuneCoordinates(null);
        return;
      }

      // If not, fetch from service
      try {
        const { fetchCommuneDetails } = await import("services/location_services");
        const details = await fetchCommuneDetails(selectedCommune, token);
        
        console.log("useLocations: fetchCommuneDetails result:", details);

        if (details?.coordinates?.center || details?.coordinates?.area) {
          console.log("useLocations: Coordinates found.", details.coordinates);
          setCommuneCoordinates(details.coordinates);
          setCommuneGeoErrorState(null);
          setErrorCommune(selectedCommune); // Match current
          // If we want to store more than just center, we can extend this
        } else {
          // No coordinates found
          console.warn("useLocations: No coordinates found in details.", details);
          setCommuneCoordinates(null);
          setCommuneGeoErrorState("La comuna seleccionada no cuenta con información geográfica");
          setErrorCommune(selectedCommune);
        }
      } catch (err) {
        console.error("useLocations: Error fetching details:", err);
        setCommuneCoordinates(null);
        setCommuneGeoErrorState("Error al obtener la información geográfica");
        setErrorCommune(selectedCommune);
      }
    };

    loadCommuneDetails();
  }, [selectedCommune, token]);

  return { regions, communes, selectedRegion, setSelectedRegion, isLoadingRegions, isLoadingCommunes, error, communeCoordinates, communeGeoError: effectiveCommuneGeoError };
};
